/**
 * Codigo publico de un evento: lo que va en la URL del cartel.
 *
 * POR QUE NO EL IDENTIFICADOR NUMERICO
 *   `/registro/3` invita a probar `/registro/2` y `/registro/4`. Hoy la API
 *   responde 404 a los eventos que no estan activos, asi que lo que se filtra es
 *   poco, pero el patron es malo igual: un numero correlativo en una direccion
 *   publica dice cuantos eventos hay, en que orden se crearon y que hay otro al
 *   lado. El dia que convivan dos gimcanas activas (una publica y otra de una
 *   escuela), enumerar las encuentra las dos.
 *
 *   El proyecto ya resuelve esto mismo para el participante, cuyo QR lleva un
 *   `qr_token` aleatorio. Esto es lo mismo un nivel mas arriba.
 *
 * POR QUE 12 CARACTERES Y NO UN UUID
 *   La direccion del cartel **se teclea**: el manual de usuario dice "escanead
 *   el QR o escribid la direccion que aparezca". Un UUID deja una URL de 86
 *   caracteres imposible de copiar a mano; con doce quedan 60 y se puede.
 *
 *   Doce caracteres de este alfabeto son 31^12, del orden de 10^17
 *   combinaciones: no se enumera ni probando sin parar durante siglos. Y el
 *   limite global de peticiones corta mucho antes.
 *
 * EL ALFABETO
 *   Minusculas y digitos SIN los que se confunden al leerlos de un cartel:
 *   fuera la o y el cero, fuera el uno, la ele y la i. Quien teclee mal un
 *   caracter recibe un 404, no el evento de otro.
 */

/** Sin `0`, `o`, `1`, `l` ni `i`: se confunden en papel. */
export const ALFABETO_CODIGO = 'abcdefghjkmnpqrstuvwxyz23456789';

export const LONGITUD_CODIGO = 12;

/**
 * Forma valida de un codigo.
 *
 * Acepta cualquier letra o digito para no rechazar los codigos que ya existan
 * (los primeros se rellenaron con hexadecimal en la migracion): esto comprueba
 * la FORMA, no que se haya generado con el alfabeto de arriba. Lo que importa
 * es que no se cuele una ruta rara ni una inyeccion por el parametro.
 */
const FORMA = /^[a-z0-9]{12}$/u;

export function esCodigoPublico(valor: unknown): valor is string {
  return typeof valor === 'string' && FORMA.test(valor);
}

/**
 * Genera un codigo nuevo.
 *
 * Usa el generador criptografico de la plataforma (`crypto.getRandomValues`),
 * disponible tanto en Node 22+ como en el navegador. `Math.random()` no vale
 * aqui: es predecible, y todo el sentido de esto es que no se pueda adivinar.
 *
 * El sesgo del modulo se evita descartando los bytes que caen en la cola: con
 * un alfabeto de 31 y 256 valores por byte, se aceptan los menores que 248.
 */
export function generarCodigoPublico(longitud: number = LONGITUD_CODIGO): string {
  const tope = 256 - (256 % ALFABETO_CODIGO.length);
  let codigo = '';

  while (codigo.length < longitud) {
    const bytes = new Uint8Array(longitud * 2);
    crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      if (byte >= tope) continue;
      codigo += ALFABETO_CODIGO[byte % ALFABETO_CODIGO.length];
      if (codigo.length === longitud) break;
    }
  }

  return codigo;
}
